import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { isXsMobile, isDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import { getCookie, deleteCookie } from '@zola-helpers/client/dist/es/util/storage';
import {
  AccountVendorDashboardView,
  StarterPackCardView,
  StarterPackRequest,
  StarterPackView,
  USCityView,
} from '@zola/svc-marketplace-ts-types';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { EditIcon, SwitchIcon } from '@zola/zola-ui/src/components/SvgIcons';
import Tooltip from '@zola/zola-ui/src/components/Tooltip/Tooltip';
import useForceUpdateOnWindowResize from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';

import cx from 'classnames';

import { receivedStarterPack } from '~/actions/couples/types/VendorStarterPacksActionTypes';
import BookVendorModal from '~/components/common/modals/BookVendorModal';
import { useModal } from '~/contexts/ModalContext';
import { useUserContext } from '~/contexts/UserContext';
import useVendorTeam, { fetchVendorTeamFn } from '~/hooks/useVendorTeam';
import BookedTile from '~/pages/couples/bookedVendors/components/BookedTile/BookedTile';
import { useAppDispatch, useAppSelector, AppDispatch } from '~/reducers';
import { getCurrentStarterPack } from '~/reducers/couples/vendorStarterPacksReducer';
import { getVendorTeam } from '~/selectors/vendorTeamSelectors';
import { VendorCardView } from '~/types/responseTypes';
import ApiService from '~/util/apiService';
import { StandardLogFn } from '~/util/logger';
import { centsToDollars } from '~/util/priceConversion';
import TextUtils from '~/util/textUtils';
import {
  mapVendorCardToProduct,
  TrackingProductLocation,
  trackProductListViewed2,
} from '~/util/trackingHelper';

import { NotNullMembersBriefLocation } from '../../util/types';
import VendorStarterPackCard from './VendorStarterPackCard';
import VendorStarterPackFormModal from './VendorStarterPackForm';

import styles from './vendorStarterPack.module.less';

const BOOKED_VENDOR_ONBOARDING_COMPLETED_COOKIE = 'BOOKED_VENDOR_ONBOARDING_COMPLETED';

const isBookedVendor = (vendor: VendorCardView | StarterPackCardView): vendor is VendorCardView => {
  if ('storefront' in vendor) {
    return false;
  }
  return true;
};

const mapCardsToProductList = (cards: (VendorCardView | StarterPackCardView)[]) => {
  return cards.map((vendor, index) => {
    if (isBookedVendor(vendor)) {
      return {
        ...mapVendorCardToProduct(vendor, index + 1),
        coupon: ['BOOKED' as const],
      };
    }
    return mapVendorCardToProduct(vendor.storefront, index + 1);
  });
};

const formatToDollars = (price: number | null) => TextUtils.formatAsCurrency(centsToDollars(price));

export type VendorStarterPackContentProps = {
  starterPack: StarterPackView;
  currentVendorMarketId?: number | null;
  vendorTeamDashboard: AccountVendorDashboardView[];
  setCurrentVendorMarketId: (vendorMarketId: number | null) => void;
  setCurrentCity: (city: USCityView) => void;
  setStarterPack: (starterPack: StarterPackView) => void;
  currentCity?: USCityView;
  vendorTeamDashboardLoaded: boolean;
  hasShuffled: boolean;
  setHasShuffled: (shuffled: boolean) => void;
};

export const VendorStarterPackContent = (props: VendorStarterPackContentProps) => {
  const {
    starterPack,
    currentCity,
    setStarterPack,
    setCurrentVendorMarketId,
    currentVendorMarketId,
    setCurrentCity,
    vendorTeamDashboard,
    vendorTeamDashboardLoaded,
    hasShuffled,
    setHasShuffled,
  } = props;

  const { serviceLevel, totalStartingPriceCents } = starterPack || {};

  useForceUpdateOnWindowResize();
  const dispatch = useAppDispatch();
  const { showCustomModal } = useModal();
  const userContext = useUserContext();
  const mobile = isXsMobile();

  const [showAllCards, setShowAllCards] = useState(!mobile);
  const [cardsToDisplay, setCardsToDisplay] = useState<(VendorCardView | StarterPackCardView)[]>(
    []
  );
  const [startingPriceCents, setStartingPriceCents] = useState(totalStartingPriceCents);

  const formattedStartingPrice = startingPriceCents ? formatToDollars(startingPriceCents) : null;

  const { is_guest: isGuest } = userContext || {};

  useEffect(() => {
    if (vendorTeamDashboardLoaded || (userContext && isGuest)) {
      const vendorCardsToDisplay = showAllCards ? starterPack.cards : starterPack.cards.slice(0, 3);

      const bookedVendorsByType = new Map(
        vendorTeamDashboard
          .filter((vendor) => !!vendor?.accountVendor?.booked)
          .map((bookedVendor) => [bookedVendor.accountVendor?.vendorType, bookedVendor])
      );

      const cards = vendorCardsToDisplay.map((vendor) => {
        const bookedVendor = bookedVendorsByType.get(vendor.vendorType);
        if (bookedVendor && bookedVendor.accountVendor?.vendorCard) {
          // Cast needed because we've mixed up svc-marketplace-types and response types
          return bookedVendor.accountVendor.vendorCard as VendorCardView;
        }
        return vendor;
      });
      setCardsToDisplay(cards);
      setStartingPriceCents(totalStartingPriceCents);

      const trackingPrice = formatToDollars(totalStartingPriceCents);

      trackProductListViewed2({
        location: TrackingProductLocation.VENDOR_STARTER_PACKS,
        products: mapCardsToProductList(cards),
        citySlug: currentCity?.slug,
        isShuffled: hasShuffled,
        startingPrice: Number(trackingPrice?.replace(/[^0-9.]+/g, '')),
      });
    }
  }, [
    showAllCards,
    vendorTeamDashboard,
    starterPack,
    vendorTeamDashboardLoaded,
    userContext,
    totalStartingPriceCents,
    currentCity?.slug,
    hasShuffled,
    isGuest,
  ]);

  useEffect(() => {
    setShowAllCards(!mobile);
  }, [mobile]);

  const changeCity = (city: USCityView, marketId: number | null) => {
    setCurrentVendorMarketId(marketId);
    setCurrentCity(city);
    getNewPack(marketId, null, setStarterPack, dispatch);
  };

  const launchStarterPackForm = () => {
    showCustomModal(
      VendorStarterPackFormModal,
      {
        currentCity,
        changeCity,
      },
      {
        className: styles.vendorStarterPackFormModal,
        mobileHalfSheet: true,
      }
    );
  };

  const showBookingModal = useCallback(() => {
    showCustomModal(BookVendorModal, {
      handleSuccess: () => {
        fetchVendorTeamFn(dispatch);
      },
      section: 'LANDING_PAGE',
      location: TrackingProductLocation.VENDOR_STARTER_PACKS,
    });
  }, [dispatch, showCustomModal]);

  const onBookedVendorsClick = () => {
    if (isGuest) {
      window.location.assign('/wedding/onboard/booked-vendor');
    } else {
      showBookingModal();
    }
  };

  const budgetText = startingPriceCents ? `Starting price: ${formattedStartingPrice}` : '';

  const resultsHed = useMemo(() => {
    return currentCity ? `${currentCity?.name}, ${currentCity?.stateCode}` : 'New York, NY'; // Default to NYC
  }, [currentCity]);

  useEffect(() => {
    if (getCookie(BOOKED_VENDOR_ONBOARDING_COMPLETED_COOKIE)) {
      deleteCookie(BOOKED_VENDOR_ONBOARDING_COMPLETED_COOKIE);
      if (!isGuest) {
        showBookingModal();
      }
    }
  }, [isGuest, showBookingModal]);

  return (
    <Fragment>
      <div className={styles.header}>
        <h2 className={styles.hed}>
          Shuffle your way to your <strong>dream team</strong>
        </h2>
        <div className={styles.dek}>
          <p>
            Discover local vendors to bring your wedding to life.{' '}
            <LinkV2
              className={styles.bookedCta}
              type="button"
              role="button"
              onClick={onBookedVendorsClick}
            >
              Already booked? Add them here
            </LinkV2>
          </p>
        </div>
      </div>
      {starterPack && starterPack.cards && (
        <div className={styles.resultsWrapper}>
          <div className={styles.resultsHeader}>
            <div className={styles.weddingLocation}>
              <span className={styles.bullet} aria-hidden>
                &bull;
              </span>
              Wedding in{' '}
              <LinkV2
                type="button"
                role="button"
                onClick={launchStarterPackForm}
                className={styles.modalCta}
              >
                {resultsHed}
                <EditIcon aria-hidden />
                {isDesktop() && <Tooltip className={styles.tooltip} text="Edit your location" />}
              </LinkV2>
            </div>
            {budgetText && (
              <div className={styles.weddingBudget}>
                <span className={styles.bullet} aria-hidden>
                  &bull;
                </span>
                <span className={styles.divider} aria-hidden>
                  |
                </span>
                {budgetText}
              </div>
            )}
            <div className={styles.swapButton}>
              <ButtonV3
                className={styles.swapTeam}
                variant="secondary"
                colorway="bay"
                size="large"
                onClick={() => {
                  getNewPack(currentVendorMarketId, null, setStarterPack, dispatch);
                  setHasShuffled(true);
                }}
              >
                <SwitchIcon width={20} height={20} aria-hidden />
                Shuffle vendors
              </ButtonV3>
            </div>
          </div>

          <div
            className={styles.vendorStarterPackResultsContainer}
            data-testid="VendorStarterPackResults"
          >
            {cardsToDisplay.map((vendor, i) => {
              if (isBookedVendor(vendor)) {
                return (
                  <BookedTile
                    vendorType={vendor.taxonomyNode?.key as VendorTaxonomyKey}
                    vendor={vendor}
                    vendorName={vendor.vendorName}
                    simple
                    key={i}
                    position={i}
                  />
                );
              }
              return (
                <VendorStarterPackCard
                  serviceLevel={serviceLevel}
                  vendor={vendor}
                  vendorMarketId={currentVendorMarketId}
                  currentCity={currentCity}
                  startingPriceCents={startingPriceCents}
                  setStartingPriceCents={setStartingPriceCents}
                  index={i}
                  key={i}
                />
              );
            })}
          </div>
          {isXsMobile() && (
            <ButtonV3
              className={styles.showMore}
              component="button"
              variant="secondary"
              colorway="bay"
              onClick={() => {
                setShowAllCards(!showAllCards);
              }}
            >
              {showAllCards ? 'Show less' : 'Show more'}
            </ButtonV3>
          )}
        </div>
      )}
    </Fragment>
  );
};

const getNewPack = (
  marketId: number | null | undefined,
  weddingBudgetCents: number | null,
  setStarterPack: (pack: StarterPackView) => void,
  dispatch: AppDispatch
) => {
  const request: StarterPackRequest = {
    vendorMarketId: marketId || 1064, // default to NYC
    weddingBudgetCents,
  };
  ApiService.post<StarterPackView, StarterPackRequest>(
    '/web-marketplace-api/v1/starter-packs/fetch-pack',
    request
  )
    .then((response) => {
      setStarterPack(response);
      dispatch(receivedStarterPack(response));
    })
    .catch(StandardLogFn);
};

export type VendorStarterPackProps = {
  vendorMarketId?: number | null;
  weddingLocation?: NotNullMembersBriefLocation;
  userDataLoaded: boolean;
};

export const VendorStarterPack = (props: VendorStarterPackProps): JSX.Element => {
  const { weddingLocation, vendorMarketId, userDataLoaded } = props;
  const storedStarterPack: StarterPackView | null = useAppSelector(getCurrentStarterPack);
  const dispatch = useAppDispatch();

  useVendorTeam();

  const { vendorTeamDashboard, loaded } = useAppSelector(getVendorTeam) || {};

  const [starterPack, setStarterPack] = useState<StarterPackView>();
  const [currentVendorMarketId, setCurrentVendorMarketId] = useState(vendorMarketId);
  const [currentCity, setCurrentCity] = useState(weddingLocation as USCityView);
  const [hasShuffled, setHasShuffled] = useState(false);

  useEffect(() => {
    if (storedStarterPack) {
      setStarterPack(storedStarterPack);
    }
  }, [storedStarterPack]);

  useEffect(() => {
    if (userDataLoaded && currentVendorMarketId && !storedStarterPack) {
      getNewPack(currentVendorMarketId, null, setStarterPack, dispatch);
    }
  }, [currentVendorMarketId, userDataLoaded, dispatch, storedStarterPack]);

  return (
    <div className={styles.vendorStarterPack}>
      <div className={cx(styles.starterPackWrapper, styles.wide)} data-testid="VendorStarterPack">
        {starterPack && (
          <VendorStarterPackContent
            starterPack={starterPack}
            currentCity={currentCity}
            vendorTeamDashboard={vendorTeamDashboard}
            setStarterPack={setStarterPack}
            setCurrentVendorMarketId={setCurrentVendorMarketId}
            currentVendorMarketId={currentVendorMarketId}
            setCurrentCity={setCurrentCity}
            vendorTeamDashboardLoaded={loaded}
            hasShuffled={hasShuffled}
            setHasShuffled={setHasShuffled}
          />
        )}
      </div>
    </div>
  );
};

export default VendorStarterPack;
